import { z } from 'zod';

export const TweakControlSchema = z.union([
  z.object({
    name: z.string().regex(/^--/),
    label: z.string(),
    type: z.literal('color'),
    default: z.string(),
  }),
  z.object({
    name: z.string().regex(/^--/),
    label: z.string(),
    type: z.literal('length'),
    default: z.union([z.string(), z.number()]),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
    unit: z.enum(['px', 'rem', 'em', '%']).optional(),
  }),
  z.object({
    name: z.string().regex(/^--/),
    label: z.string(),
    type: z.literal('number'),
    default: z.union([z.string(), z.number()]),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
  }),
  z.object({
    name: z.string().regex(/^--/),
    label: z.string(),
    type: z.literal('select'),
    default: z.string(),
    options: z.array(z.string()).min(1),
  }),
]);
export type TweakControl = z.infer<typeof TweakControlSchema>;

export const TweakManifestSchema = z.object({
  controls: z.array(TweakControlSchema),
});
export type TweakManifest = z.infer<typeof TweakManifestSchema>;

const SCRIPT_RE = /<script[^>]+id=["']cdc-tweaks["'][^>]*>([\s\S]*?)<\/script>/i;

export function extractTweakManifest(html: string): TweakManifest | null {
  const match = SCRIPT_RE.exec(html);
  if (!match || !match[1]) return null;
  try {
    const json = JSON.parse(match[1].trim()) as unknown;
    const parsed = TweakManifestSchema.safeParse(json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Convert a control's `default` to a CSS-variable-ready string. */
export function defaultValue(control: TweakControl): string {
  if (control.type === 'color' || control.type === 'select') return control.default;
  const raw = control.default;
  if (typeof raw === 'string') return raw;
  if (control.type === 'length') return `${raw}${('unit' in control && control.unit) || 'px'}`;
  return String(raw);
}

/** Compose a length/number value from a numeric slider tick. */
export function composeValue(control: TweakControl, sliderValue: number): string {
  if (control.type === 'length') {
    const unit = ('unit' in control && control.unit) || 'px';
    return `${sliderValue}${unit}`;
  }
  return String(sliderValue);
}

/** Parse a length/number CSS value back to a number for slider hydration. */
export function parseNumeric(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const m = /-?\d+(\.\d+)?/.exec(value);
  return m ? Number(m[0]) : fallback;
}
